import * as amplitude from "@amplitude/analytics-browser";
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import React from "react";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useGlossary } from "src/hooks/useGlossary";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { validateWithZod } from "src/services/formValidations";
import * as GQL from "src/types/graphql";
import { z } from "zod";
import {
  ADD_PERSON_RELATIONSHIP,
  UPDATE_PERSON_RELATIONSHIP,
} from "../graphql/mutations";
import { GET_PERSON_RELATIONSHIPS } from "../graphql/queries";
import { PersonSelectInput } from "../Inputs/PersonSelectInput";

const FormSchema = z.object({
  relationshipId: z.string().min(36, "This field is required"),
});

type FormType = z.infer<typeof FormSchema>;
type Props = {
  id: string;
  isOpen: boolean;
  personType: "applicant" | "guardian";
  onCancel: () => void;
  onAddRelationshipSuccess: () => void;
};
export const AddRelationshipDialog: React.FC<Props> = ({
  id,
  isOpen,
  personType,
  onCancel,
  onAddRelationshipSuccess,
}) => {
  const toast = useAvelaToast();
  const { glossary } = useGlossary();
  const organization = useOrganization();
  const organizationId = organization.map((org) => org.id).withDefault("");

  const initialValues = {
    relationshipId: "",
  };

  const [updatePersonRelationship] = useRemoteDataMutation<
    GQL.UpdatePersonRelationship,
    GQL.UpdatePersonRelationshipVariables
  >(UPDATE_PERSON_RELATIONSHIP, {
    refetchQueries: [GET_PERSON_RELATIONSHIPS],
  });

  const [addPersonRelationship] = useRemoteDataMutation<
    GQL.AddPersonRelationship,
    GQL.AddPersonRelationshipVariables
  >(ADD_PERSON_RELATIONSHIP, {
    refetchQueries: [GET_PERSON_RELATIONSHIPS],
  });

  const onSubmit = React.useCallback(
    async (values: FormType, formikHelpers: FormikHelpers<FormType>) => {
      try {
        // first try to update the relationship in the other direction
        const updateResult = await updatePersonRelationship({
          variables: {
            first_person: values.relationshipId,
            second_person: id,
          },
        });

        const hasUpdated =
          updateResult.data?.update_relationship_person?.affected_rows &&
          updateResult.data.update_relationship_person.affected_rows > 0;

        if (!hasUpdated) {
          await addPersonRelationship({
            variables: {
              first_person: id,
              second_person: values.relationshipId,
            },
          });
        }

        onAddRelationshipSuccess();

        if (!toast.isActive(id)) {
          toast({
            id,
            title: glossary`Relationship added`,
            isClosable: true,
          });
        }
      } catch (error: any) {
        if (
          error?.graphQLErrors?.length > 0 &&
          error?.graphQLErrors[0]?.extensions?.code === "constraint-violation"
        ) {
          formikHelpers.setFieldError(
            "relationshipId",
            `${
              personType === "guardian" ? "Student" : "Parent"
            } relationship already exists`
          );
        } else if (!toast.isActive(id)) {
          toast({
            id,
            title: glossary`Error adding relationship`,
            description:
              "Please try again later or report the problem to our support team.",
            isClosable: true,
            status: "error",
          });
        }
      }
    },
    [
      id,
      toast,
      updatePersonRelationship,
      addPersonRelationship,
      onAddRelationshipSuccess,
      glossary,
      personType,
    ]
  );

  const onCancelButtonClick = React.useCallback(
    (formik: FormikProps<FormType>) => {
      formik.resetForm();
      onCancel();
    },
    [onCancel]
  );
  return (
    <Formik<FormType>
      initialValues={initialValues}
      enableReinitialize
      validateOnBlur={false}
      onSubmit={onSubmit}
      validate={validateWithZod(FormSchema)}
    >
      {(formik) => {
        return (
          <Modal isOpen={isOpen} onClose={onCancel} isCentered>
            <ModalOverlay />
            <ModalContent as={Form}>
              <ModalHeader>Add relationship</ModalHeader>
              <ModalBody as={Flex} direction="column" gap={4}>
                <PersonSelectInput
                  name="relationshipId"
                  label={personType === "guardian" ? "Student" : "Guardian"}
                  organizationId={organizationId}
                  personType={
                    personType === "guardian" ? "applicant" : "guardian"
                  }
                  isRequired={true}
                />
              </ModalBody>
              <ModalFooter gap={2}>
                <Button
                  variant="ghost"
                  colorScheme="gray"
                  onClick={() => {
                    amplitude.track("Cancel relationship clicked");
                    onCancelButtonClick(formik);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  onClick={() => {
                    amplitude.track("Add relationship clicked");
                  }}
                >
                  Add relationship
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      }}
    </Formik>
  );
};
